import { useMemo } from 'react';
import { HEADING_SIZE_CLASSES, SECTION_PADDING_CLASSES } from './commonDefaults';
import { isLightColor, adjustColor } from '../../themeDefaults';

/**
 * useBlockStyles — Resolves per-block style overrides against the site theme.
 *
 * Fallback chain per property:
 *   content.style.X  →  content.X (legacy)  →  options.defaultX  →  theme value  →  hardcoded
 *
 * @param {object} content  - block.content
 * @param {object} theme    - resolvedTheme from themeDefaults.getResolvedTheme()
 * @param {object} options  - static options object (define at module level!)
 *   options.defaultHeadingSize    - 'small' | 'medium' | 'large' | 'xlarge'
 *   options.defaultAlignment      - 'left' | 'center' | 'right'
 *   options.defaultButtonStyle    - 'primary' | 'secondary' | 'outline'
 *   options.defaultPadding        - 'compact' | 'normal' | 'spacious'
 *   options.defaultBackground     - 'section' | 'card' | 'page' | 'transparent'
 *
 * @returns {object} Resolved style values + utility classes + helpers
 */
const useBlockStyles = (content = {}, theme = {}, options = {}) => {
  // Destructure primitives so useMemo deps are stable
  const style = content.style || {};
  const colors = theme.colors || {};
  const preset = theme.preset || 'dark';
  const themeHeadingFont = theme.fonts?.heading || 'Inter';
  const themeBodyFont = theme.fonts?.body || 'Inter';

  const {
    defaultHeadingSize = 'medium',
    defaultAlignment = 'center',
    defaultButtonStyle = 'primary',
    defaultPadding = 'normal',
    defaultBackground = 'section',
  } = options;

  return useMemo(() => {
    // ── Helper: resolve a single property through the fallback chain ──
    const resolve = (prop, legacyProp, themeValue, hardcoded) => {
      // 1. style.X (new system)
      if (style[prop] != null) return style[prop];
      // 2. content.X (legacy — e.g. content.headingSize)
      if (legacyProp != null && content[legacyProp] != null) return content[legacyProp];
      // 3. theme value
      if (themeValue != null) return themeValue;
      // 4. hardcoded fallback
      return hardcoded;
    };

    // ── Resolve each property ──────────────────────────────────────────

    // Background map for the defaultBackground option
    const bgMap = {
      section: colors.sectionBackground || '#1a1a2e',
      card: colors.cardBackground || '#1e1e2e',
      page: colors.pageBackground || '#0d0d0d',
      transparent: 'transparent',
    };

    const sectionBackground = resolve(
      'sectionBackground', null, null, bgMap[defaultBackground] || bgMap.section
    );
    const cardBackground = resolve(
      'cardBackground', null, colors.cardBackground, '#1e1e2e'
    );
    const headingColor = resolve(
      'headingColor', null, colors.headingColor, '#ffffff'
    );
    const bodyColor = resolve(
      'bodyColor', null, colors.bodyColor, '#ffffff'
    );
    const accentColor = resolve(
      'accentColor', null, colors.accent, '#749555'
    );
    const inputBackground = resolve(
      'inputBackground', null, null, preset === 'dark' ? '#252540' : '#ffffff'
    );
    const mutedColor = colors.mutedColor || '#94a3b8';

    const headingSize = resolve(
      'headingSize', 'headingSize', null, defaultHeadingSize
    );
    const headingAlignment = resolve(
      'headingAlignment', 'alignment', null, defaultAlignment
    );
    const buttonStyle = resolve(
      'buttonStyle', 'buttonStyle', null, defaultButtonStyle
    );
    const sectionPadding = resolve(
      'sectionPadding', null, null, defaultPadding
    );

    // Fonts: style override → theme font → 'Inter'
    const headingFont = style.headingFont || themeHeadingFont;
    const bodyFont = style.bodyFont || themeBodyFont;

    const buttonColor = resolve(
      'buttonColor', null, null, accentColor
    );

    // Button text color: style override → theme → auto-detect from button bg
    const resolvedButtonBg = buttonColor || accentColor;
    const autoButtonTextColor = isLightColor(resolvedButtonBg) ? '#1a1a2e' : '#ffffff';
    const buttonTextColor = style.buttonTextColor || colors.buttonTextColor || autoButtonTextColor;

    // ── Derived values ─────────────────────────────────────────────────

    const isDark = preset === 'dark';
    const borderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    const accentGradient = colors.accentGradient || null;

    // ── Utility classes ────────────────────────────────────────────────

    const headingSizeClass = HEADING_SIZE_CLASSES[headingSize] || HEADING_SIZE_CLASSES.medium;
    const sectionPaddingClass = SECTION_PADDING_CLASSES[sectionPadding] || SECTION_PADDING_CLASSES.normal;

    const headingAlignmentClass = {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    }[headingAlignment] || 'text-center';

    const contentAlignmentClass = {
      left: 'text-left items-start',
      center: 'text-center items-center',
      right: 'text-right items-end',
    }[headingAlignment] || 'text-center items-center';

    // ── Button style helper ────────────────────────────────────────────

    const getButtonStyles = () => {
      const resolvedButtonColor = buttonColor || accentColor;
      const resolvedGradient = accentGradient;

      if (buttonStyle === 'primary') {
        if (style.buttonColor) {
          // Per-block button color override — use solid
          return { backgroundColor: resolvedButtonColor, color: buttonTextColor };
        }
        if (resolvedGradient) {
          return { background: resolvedGradient, color: buttonTextColor };
        }
        return { backgroundColor: resolvedButtonColor, color: buttonTextColor };
      }
      if (buttonStyle === 'secondary') {
        return {
          backgroundColor: isDark ? '#2a2a3e' : '#f3f4f6',
          color: headingColor,
          border: `1px solid ${borderColor}`,
        };
      }
      // outline
      return {
        backgroundColor: 'transparent',
        color: resolvedButtonColor,
        border: `2px solid ${resolvedButtonColor}`,
      };
    };

    // ── Heading font style (inline) ────────────────────────────────────

    const headingFontStyle = headingFont
      ? `"${headingFont}", system-ui, sans-serif`
      : 'var(--theme-font-heading, inherit)';

    const bodyFontStyle = bodyFont
      ? `"${bodyFont}", system-ui, sans-serif`
      : 'var(--theme-font-body, inherit)';

    return {
      // Raw resolved values
      sectionBackground,
      cardBackground,
      headingColor,
      bodyColor,
      accentColor,
      inputBackground,
      mutedColor,
      headingSize,
      headingAlignment,
      buttonStyle,
      buttonColor,
      buttonTextColor,
      sectionPadding,
      headingFont,
      bodyFont,
      accentGradient,

      // Derived
      isDark,
      borderColor,

      // Utility classes
      headingSizeClass,
      sectionPaddingClass,
      headingAlignmentClass,
      contentAlignmentClass,

      // Inline style strings
      headingFontStyle,
      bodyFontStyle,

      // Helpers
      getButtonStyles,
    };
  }, [
    // Primitives only — keeps deps stable
    style.headingSize, style.headingAlignment, style.headingFont, style.headingColor,
    style.bodyFont, style.bodyColor,
    style.sectionBackground, style.cardBackground, style.accentColor, style.inputBackground,
    style.buttonStyle, style.buttonColor, style.buttonTextColor,
    style.sectionPadding,
    // Legacy content fields
    content.headingSize, content.alignment, content.buttonStyle,
    // Theme
    colors.sectionBackground, colors.cardBackground, colors.pageBackground,
    colors.headingColor, colors.bodyColor, colors.mutedColor,
    colors.accent, colors.accentGradient, colors.buttonTextColor,
    themeHeadingFont, themeBodyFont,
    preset,
    // Options (static, but include for correctness)
    defaultHeadingSize, defaultAlignment, defaultButtonStyle, defaultPadding, defaultBackground,
  ]);
};

export default useBlockStyles;
